import { type ReactNode, useEffect } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { LoginPage } from './LoginPage'
import { Loader2 } from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom'

interface AuthGuardProps {
  children: ReactNode
}

export function AuthGuard({ children }: AuthGuardProps) {
  const { isAuthenticated, isLoading } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    // If not authenticated and not on login page, update URL to /login
    if (!isLoading && !isAuthenticated && location.pathname !== '/login') {
      navigate('/login', { replace: true, state: { from: location.pathname } })
    }
    // If authenticated and on login page, redirect to root or previous page
    else if (!isLoading && isAuthenticated && location.pathname === '/login') {
      const from = location.state?.from || '/'
      navigate(from, { replace: true })
    }
  }, [isAuthenticated, isLoading, location, navigate])

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    )
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <LoginPage />
  }

  // Show protected content if authenticated
  return <>{children}</>
}